/* Core & libraries */
import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "react-jss";
import Select, { components } from 'react-select';
import { FormattedMessage } from "react-intl";

/* Helpers, constants, custom methods, internal libraries */

/* Components */
/* Styles */
import { reactSelectStyles, disabledStyles } from "./styles.js";
import DynamicImageLoader from "../dynamicImageLoader";
import { colors } from "../../constants/colors";

const UCSelect = props => {
  const { className = "", displayName, options, onChange, onBlur, defaultValue, styles, onMenuOpen, onMenuClose, getOptionLabel, getOptionValue, isOptionSelected, value, disabled, key, isSearchable = false, customSelectedValue, filterOption, shouldOpenUp, ariaLabel, formatOptionLabel, ariaDescribedBy, isMulti = false, error  } = props;
  const { Option } = components;
  const [selectedValue, setSelectedValue] = useState(null);
  const theme = useTheme();
  const multiContainerRef = useRef(null);
  const selectRef = useRef(null);
  const [multiValue, setMultiValue] = useState(defaultValue);
  const [isOpenMulti, setIsOpenMulti] = useState(false);
  const ALL_VALUE = 'All';

   const serializedDefault = isMulti
    ? JSON.stringify((defaultValue || []).map(d => d?.optionValue ?? d?.value ?? d))
    : null;

  useEffect(() => {
    if (isMulti && Array.isArray(defaultValue) && defaultValue.length > 0) {
      // normalize default so if 'All' is present it becomes the sole selection
      const hasAll = (defaultValue || []).some(d => (getOptionValue ? getOptionValue(d) : (d.optionValue ?? d.value)) === ALL_VALUE);
      if (hasAll) {
        const allOpt = (defaultValue || []).find(d => (getOptionValue ? getOptionValue(d) : (d.optionValue ?? d.value)) === ALL_VALUE);
        setMultiValue(allOpt ? [allOpt] : []);
      } else {
        setMultiValue(defaultValue);
      }
    }
  }, [serializedDefault]);


  useEffect(() => {
    setSelectedValue(null)
    const selVal = options.find(item => item.value === value)

    {
      selVal ?
        setSelectedValue(selVal) :
        setSelectedValue({value:" ", label:props.displayName})
    }
  }, [value, defaultValue]);

    useEffect(() => {
    const handleOutsideClick = (e) => {
      if (multiContainerRef.current && !multiContainerRef.current.contains(e.target)) {
        setIsOpenMulti(false);
      }
    };

     document.addEventListener('mousedown', handleOutsideClick);
  return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

const renderOptionLabel = (option) => {
    if (getOptionLabel) {
      return getOptionLabel(option);
    }
    if (option.labelId) {
      return <FormattedMessage id={option.labelId} defaultMessage={option.label} />;
    }
    return option.label;
  };

  const SelectedOption = props => (
      <Option
        {...props}
      >
        {renderOptionLabel(props.data)}
        <DynamicImageLoader imageName={"select-blue-checkmark.svg"} />
      </Option>
    );

  const CustomSingleValue = ({ children, ...props }) => (
    <components.SingleValue {...props}>
      {customSelectedValue ? props.data[customSelectedValue] : renderOptionLabel(props.data)}
    </components.SingleValue>
  );

 const multiSelectedOption = props => {
  const { data, selectProps, isFocused } = props;

  const optionVal = getOptionValue ? getOptionValue(data) : (data.optionValue ?? data.value);

  // determine the first option from the current options array
  const firstOption = selectProps?.options && selectProps.options.length > 0 ? selectProps.options[0] : null;
  const firstOptionVal = firstOption ? (getOptionValue ? getOptionValue(firstOption) : (firstOption.optionValue ?? firstOption.value)) : null;
  const isFirst = optionVal === firstOptionVal;

  const isActuallySelected = Array.isArray(multiValue) && multiValue.some(o => (getOptionValue ? getOptionValue(o) : (o.optionValue ?? o.value)) === optionVal);


  const borderBottom = optionVal === ALL_VALUE ? `1px solid ${colors.lightGrey}` : 'none';
  const backgroundColor = isActuallySelected ? undefined : 'transparent';

  const inlineStyle = {
    borderBottom,
    backgroundColor
  };

  const innerProps = {
    ...props.innerProps,
    style: {
      ...(props.innerProps && props.innerProps.style ? props.innerProps.style : {}),
      ...inlineStyle
    }
  };

  return (
    <Option
      {...props}
      innerProps={innerProps}
      className={isFirst ? 'ucselect-first-option' : undefined}
    >
      <div>
        <span>{ getOptionLabel ? getOptionLabel(data) : data.optionText }</span>
        { isActuallySelected && <DynamicImageLoader imageName={"select-blue-checkmark.svg"} /> }
      </div>
    </Option>
  );
};

   const handleMenuOpen = () => {
    if (isOpenMulti) {
    setIsOpenMulti(false);  // toggle off if already open
  } else {
    setIsOpenMulti(true); 
    if (onMenuOpen) onMenuOpen(); 
  } 
  };


   const handleMultiChange = (selected) => {
   if(!selected || selected.length === 0){
    setMultiValue([]);
    onChange && onChange([]);
    return;
   }

   
   const isSelectAllLastSelected = selected[selected.length - 1];
   if(isSelectAllLastSelected && isSelectAllLastSelected.optionValue === ALL_VALUE){
    setMultiValue([isSelectAllLastSelected]);
    onChange && onChange([isSelectAllLastSelected]);
    return;
   }

   const newSelected = selected.filter(option => option.optionValue !== ALL_VALUE);
   setMultiValue(newSelected);
   onChange && onChange(newSelected);

  };

  const multiValueContainer = (props) => {
    const { children, selectProps } = props;
    const selected = selectProps.value || null;


    if(selected === null || selected.length === 0){
      return (
        <components.ValueContainer {...props}>
          {children}
        </components.ValueContainer>
      );
    }

    if(selected.length > 0 && selected.some(option => option.optionValue === ALL_VALUE)){
      const allOption = selected.find(option => option.optionValue === ALL_VALUE);
   return (
     <components.ValueContainer {...props}>
      {allOption.optionText}
      {children}
     </components.ValueContainer>
    );
    }
    if(selected.length > 0 &&  !selected.some(option => option.optionValue === ALL_VALUE)){
    const first = selected[0];
    const remainingCount = selected.length -1;


    return (
      <components.ValueContainer {...props}>
       {remainingCount > 0 ? ( <span> {first.optionText} + {remainingCount} </span> ) : (first.optionText)  }
       {children}
      </components.ValueContainer>
    );
  }

  return (
        <components.ValueContainer {...props}>
          {selectProps.placeholder}
          {children}
        </components.ValueContainer>
      );

  }

const handleControlMouseDown = (e) => {
  e.preventDefault();
  e.stopPropagation();
  setIsOpenMulti(prev => !prev);
};

const MultiDropdownIndicator = (props) => (
  <components.DropdownIndicator {...props} innerProps={{
    ...props.innerProps,
    onMouseDown: handleControlMouseDown
  }} />
);

const MultiControl = (props) => (
  <components.Control {...props} innerProps={{
    ...props.innerProps,
    onMouseDown: handleControlMouseDown
  }} />
);
  return (
     isMulti
      ?
      <div ref={multiContainerRef}
  >
        <Select
          ref={selectRef}
          isMulti
          options={options}
          value={multiValue}
          key={key}
          onChange={handleMultiChange}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          menuPlacement={shouldOpenUp ? 'top' : 'bottom'}
          menuIsOpen={isOpenMulti}
          onMenuOpen={handleMenuOpen}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          openMenuOnClick={true}
          openMenuOnFocus={false}
          placeholder={displayName}
          styles={ disabled ? {...disabledStyles(theme), ...styles} : {...reactSelectStyles(theme, error), ...styles }}
          className={className}
          components={{
            IndicatorSeparator: () => null,
            ClearIndicator: () => null,
            Option: multiSelectedOption,
            DropdownIndicator: MultiDropdownIndicator,
            ValueContainer: multiValueContainer,
            MultiValue: () => null,
            MultiValueRemove: () => null,
            Control: MultiControl
          }}
        />
      </div>
      :
    <Select
      isSearchable={isSearchable}
      key={key}
      styles={ disabled ? {...disabledStyles(theme), ...styles} : {...reactSelectStyles(theme, error), ...styles }}
      placeholder={displayName}
      className={className}
      options={options}
      components={{
        IndicatorSeparator: () => null,
        Option: SelectedOption,
        SingleValue: CustomSingleValue
      }}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      onBlur={onBlur}
      onChange={onChange}
      //menuIsOpen={true}
      defaultValue={defaultValue}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      isOptionSelected={isOptionSelected}
      value={props.value ? selectedValue : value}
      isDisabled={disabled}
      filterOption={filterOption}
      menuPlacement={shouldOpenUp ? 'top' : 'bottom'}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      formatOptionLabel={formatOptionLabel}
    />
  )
}

export default UCSelect;